import pino, { Logger, LogFn } from 'pino';
import errorToJson from 'error-to-json';
import { getWriter } from './writers';
import { ROOT_LOG_NAME } from './types';

export { errorToJson };

const rootLogger = pino({
  level: process.env.LOG_LEVEL || 'info',
  browser: {
    write: getWriter(console)
  }
});

/**
 * Create a logger with a specific name
 *
 * @param name The name of the logger (it will be prefixed with <rootLogName>
 * and written to every logged line (in the `$.@n` field)
 * @param fixedArgs Optional structured log data.  This data is written to every
 * logged line (as JSON, in the `$.args` field)
 * @returns An initialised pino Logger
 */
export const getLogger = (name: string, fixedArgs: object = {}): Logger => {
  const logger = rootLogger.child({ logName: `${ROOT_LOG_NAME}.${name}` });
  return wrappedLogger(logger, fixedArgs);
};

/**
 * Wrap the given Logger so that it's LogFn's also log a fixed set of
 * args
 *
 * @param logger The Logger to wrap
 * @param fixedArgs The object to add to `$.args` in every log line
 */
const wrappedLogger = (logger: Logger, fixedArgs: object): Logger => {
  logger.error = wrappedLogFn(logger, logger.error, fixedArgs);
  logger.warn = wrappedLogFn(logger, logger.warn, fixedArgs);
  logger.info = wrappedLogFn(logger, logger.info, fixedArgs);
  logger.debug = wrappedLogFn(logger, logger.debug, fixedArgs);
  logger.trace = wrappedLogFn(logger, logger.trace, fixedArgs);
  return logger;
};

/**
 *
 * @param logger The Logger that owns the `loggerFn`
 * @param loggerFn The LogFn to wrap
 * @param fixedArgs The object to add to `$.args` in every log line
 *
 * @see https://www.typescriptlang.org/docs/handbook/release-notes/overview.html#caveats-3
 * >Additionally, another caveat of this new functionality is that...
 * call... can’t yet fully model functions that have overloads.  When
 * using these methods on... a function with overloads, only the last
 * overload will ever be modeled.
 */
const wrappedLogFn = (logger: Logger, loggerFn: LogFn, fixedArgs: object): LogFn => {
  return (o: unknown, ...n: any[]) => {
    if (typeof o === 'object' && o !== null) {
      loggerFn.call(logger, { ...o, ...fixedArgs }, ...n);
    } else {
      // @ts-ignore: see function comment
      loggerFn.call(logger, fixedArgs, o, ...n);
    }
  };
};
